import { NavLink } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { ThemeToggle } from '../theme/ThemeToggle'
import type { NavConfig, NavItem } from './navConfig'

type Props = {
  config: NavConfig
  /** true cuando el sidebar está expandido (mostrar labels). */
  expanded: boolean
  /** true cuando el sidebar está "abierto" sobre mobile (drawer). */
  mobileOpen: boolean
  /** true si el menú está anclado por el usuario (pin, §5.8). */
  pinned: boolean
  onTogglePin: () => void
  /** Abre/cierra el sidebar (hamburguesa). */
  onToggle: () => void
  /** Cierra el drawer mobile (al navegar). */
  onNavigate: () => void
  /** Etiqueta de la zona (ej. "Portal de usuario" / "Portal de administrador") */
  zoneLabel: string
  /** Si true, oculta el toggle de pin (no aplica en mobile). */
  hidePin?: boolean
  /** El perfil al que llevará el enlace "Perfil" del menú de cuenta. */
  profilePath?: string
}

/**
 * Sidebar con 3 zonas (§5.2-5.4 SRS):
 *   - Top    : marca + botón pin (en desktop).
 *   - Primary: navegación primaria.
 *   - Middle : accesos directos fijos.
 *   - Bottom : dropdown de cuenta (avatar + nombre) que despliega:
 *              Perfil, Configuración, selector de Tema, Cerrar Sesión.
 *
 * Comportamiento (§5.7-5.8):
 *   - Desktop colapsado (no pinned, no hover): solo iconos centrados.
 *   - Desktop hover: expande temporalmente y muestra labels (gestionado por CSS :hover).
 *   - Pinned: queda expandido siempre.
 *   - Mobile: el sidebar se oculta y solo aparece como drawer (mobileOpen).
 */
export function Sidebar({
  config,
  expanded,
  mobileOpen,
  pinned,
  onTogglePin,
  onToggle,
  onNavigate,
  zoneLabel,
  hidePin = false,
  profilePath = '/perfil',
}: Props) {
  const className = [
    'sidebar',
    expanded ? 'sidebar--expanded' : 'sidebar--collapsed',
    mobileOpen ? 'sidebar--mobile-open' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <aside className={className} aria-label={zoneLabel}>
      <div className="sidebar__brand">
        <button
          type="button"
          className="sidebar__hamburger"
          onClick={onToggle}
          aria-label="Abrir o cerrar menú lateral"
        >
          {expanded
            ? <span aria-hidden="true">☰</span>
            : <img src="/icon-192.png" className="sidebar__hamburger-icon" aria-hidden="true" alt="" />}
        </button>
        <img src="/icon-192.png" className="sidebar__brand-icon" aria-hidden="true" alt="" />
        <span className="sidebar__brand-text">
          Velt <span className="accent">Intrepid</span>
        </span>
        {!hidePin && (
          <button
            type="button"
            className="sidebar__pin"
            onClick={onTogglePin}
            aria-pressed={pinned}
            title={pinned ? 'Desanclar menú' : 'Anclar menú'}
          >
            📌
          </button>
        )}
      </div>

      <nav className="sidebar__nav">
        <ul className="sidebar__zone sidebar__zone--primary">
          {config.primary.map((item) => (
            <SidebarItem key={item.path} item={item} onNavigate={onNavigate} />
          ))}
        </ul>

        {config.middle.length > 0 && (
          <>
            <hr className="sidebar__divider" />
            <ul className="sidebar__zone sidebar__zone--middle">
              {config.middle.map((item) => (
                <SidebarItem key={item.path} item={item} onNavigate={onNavigate} />
              ))}
            </ul>
          </>
        )}
      </nav>

      <div className="sidebar__bottom">
        <AccountMenu onNavigate={onNavigate} profilePath={profilePath} />
      </div>
    </aside>
  )
}

function SidebarItem({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  return (
    <li>
      <NavLink
        to={item.path}
        end={item.path === '/dashboard' || item.path === '/admin'}
        className={({ isActive }) => (isActive ? 'sidebar__link active' : 'sidebar__link')}
        onClick={onNavigate}
        title={item.label}
      >
        <span className="sidebar__link-icon" aria-hidden="true">{item.icon}</span>
        <span className="sidebar__link-label">{item.label}</span>
      </NavLink>
    </li>
  )
}

/**
 * AccountMenu (§5.4) — único elemento colapsable en la zona inferior.
 * Al hacer clic en el avatar/nombre se expande mostrando: Perfil, Configuración, Tema, Cerrar Sesión.
 */
function AccountMenu({ onNavigate, profilePath }: { onNavigate: () => void; profilePath: string }) {
  const { state, logout } = useAuth()

  if (state.status !== 'authenticated') return null

  const { user } = state
  const fullName = `${user.first_name} ${user.last_name_paternal}`
  const initials = (user.first_name[0] ?? '') + (user.last_name_paternal[0] ?? '')

  return (
    <details className="account-menu">
      <summary className="account-menu__trigger" title={fullName}>
        <span className="account-menu__avatar" aria-hidden="true">{initials.toUpperCase()}</span>
        <span className="account-menu__name">
          <strong>{fullName}</strong>
          <small>{user.email}</small>
        </span>
        <span className="account-menu__chevron" aria-hidden="true">⌃</span>
      </summary>

      <div className="account-menu__panel">
        <NavLink to={profilePath} className="account-menu__item" onClick={onNavigate}>
          <span aria-hidden="true">☺</span> Perfil
        </NavLink>
        <div className="account-menu__theme">
          <small>Tema</small>
          <ThemeToggle />
        </div>

        <button type="button" className="account-menu__item account-menu__logout" onClick={logout}>
          <span aria-hidden="true">⏻</span> Cerrar sesión
        </button>
      </div>
    </details>
  )
}
